import 'swiper/swiper-bundle.css'

import { Global } from '@emotion/react'
import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import { installGlobalHandlers } from '@mathflat/shared/@modules/errorReporting'
import { bindI18nBrand, initI18n } from '@mathflat/shared/i18n/i18n'
import { QueryClientProvider } from '@tanstack/react-query'
import { domAnimation, LazyMotion } from 'framer-motion'
import { reaction } from 'mobx'
import ReactDOM from 'react-dom/client'
import { RouterProvider } from 'react-router'
import { ToastContainer } from 'react-toastify'

import queryClient from '~/@common/queryClient'
import { commonRepo } from '~/@common/services/repo.service'
import { globalCss } from '~/@common/styles/globalCss.ts'
import { applyBrandHead } from '~/@common/utils/applyBrandHead'
import { envUtils } from '~/@common/utils/envUtils'
import { reporter } from '~/@common/utils/errorReporting/reporter'
import { initSentry } from '~/@common/utils/errorReporting/sentry'
import { testUtils } from '~/@common/utils/testUtils'
import { LastLocationManager } from '~/@pages/@common/index.ts'

import { appRouter } from './appRouter.tsx'
import { initialize } from './init/initialize.ts'

// 에러 수집은 다른 어떤 초기화보다 먼저 — 부팅 중 예외까지 잡는다. Sentry → 전역 리스너 순서.
initSentry()
installGlobalHandlers(reporter)

domainProvider.debugging({ isLiveEnv: envUtils.isLive })

if (!envUtils.isLive) {
  testUtils.initialize()
}

// 경로 축을 빼면 `/creverse/*` 진입 시 로그인 전 isCreverse === false 가 head 를 매쓰플랫으로 되돌린다.
// 경로는 MobX 가 추적하지 못하므로 라우터 구독으로 따로 갱신한다.
const syncBrandHead = () => {
  const isCreverse =
    commonRepo.isCreverse || appRouter.state.location.pathname.startsWith('/creverse/')
  applyBrandHead(isCreverse ? 'creverse' : 'default')
}
reaction(() => commonRepo.isCreverse, syncBrandHead, { fireImmediately: true })
appRouter.subscribe(syncBrandHead)

initialize()
  .then(() => initI18n())
  .then(async () => {
    const academy = commonRepo.academy
    if (!academy) return
    // 브랜드 바인딩은 비핵심 — 실패해도 default 번들로 렌더는 진행한다
    try {
      await bindI18nBrand(
        { academyGroupType: academy.academyGroupType, customerType: academy.customerType },
        academy.languagePackFileUrl,
      )
    } catch (error) {
      console.error('[i18n] 브랜드 바인딩 실패', error)
    }
  })
  .then(() => {
    ReactDOM.createRoot(document.getElementById('app') as HTMLElement).render(
      <>
        <Global styles={globalCss} />
        <QueryClientProvider client={queryClient}>
          <LazyMotion features={domAnimation} strict>
            <LastLocationManager.Provider value={new LastLocationManager.Service(appRouter)}>
              <RouterProvider router={appRouter} future={{ v7_startTransition: true }} />
            </LastLocationManager.Provider>
          </LazyMotion>
        </QueryClientProvider>
        <ToastContainer />
      </>,
    )
  })
